import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getUniId } from '@/core/utils/extractCookieId';

const BdsIcon = 'bds-icon' as any;

export interface SortOption {
  key: string;
  labelKey: string;
  icon: string;
}

export interface SortFilterProps {
  selectedKey?: string;
  eventName?: string;
  storageKeyBase?: string;
}

export const SORT_OPTIONS: SortOption[] = [
  { key: 'hottest', labelKey: 'sortBy.options.hottest', icon: 'fire' },
  { key: 'recent', labelKey: 'sortBy.options.recent', icon: 'clock' },
  { key: 'oldest', labelKey: 'sortBy.options.oldest', icon: 'date-time' },
  { key: 'aaa', labelKey: 'sortBy.options.aaa', icon: 'user-favorite' },
  { key: 'most_interactions', labelKey: 'sortBy.options.mostInteractions', icon: 'heart' },
  { key: 'most_shared', labelKey: 'sortBy.options.mostShared', icon: 'share' },
  { key: 'most_commented', labelKey: 'sortBy.options.mostCommented', icon: 'message-ballon' },
  { key: 'most_followers', labelKey: 'sortBy.options.mostFollowers', icon: 'team' },
];

const DEFAULT_SORT_STORAGE_KEY_BASE = 'MFE_PROPS_SORT_BY';

const WARROOM_TO_MFE_KEY: Record<string, string> = {
  hot: 'hottest',
  influence: 'aaa',
  date_asc: 'oldest',
  date_desc: 'recent',
  shares: 'most_shared',
  comments: 'most_commented',
  followers: 'most_followers',
  interactions: 'most_interactions',
};

export const SortFilter: React.FC<SortFilterProps> = ({ selectedKey, eventName = 'mfe-sort-by-change', storageKeyBase = DEFAULT_SORT_STORAGE_KEY_BASE }) => {
  const { t } = useTranslation();
  const storageKey = `${storageKeyBase}_${getUniId()}`;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SortOption>(() => {
    if (selectedKey) {
      return SORT_OPTIONS.find((option) => option.key === selectedKey) ?? SORT_OPTIONS[0];
    }
    const storedWarroomValue = localStorage.getItem(storageKey);
    if (storedWarroomValue) {
      const mfeKey = WARROOM_TO_MFE_KEY[storedWarroomValue];
      if (mfeKey) {
        const stored = SORT_OPTIONS.find((option) => option.key === mfeKey);
        if (stored) return stored;
      }
    }
    return SORT_OPTIONS[0];
  });

  const containerReference = useRef<HTMLDivElement>(null);

  const dispatchCustomEvent = useCallback((eventName: string, detail?: unknown) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }, []);

  const handleOptionSelect = useCallback((option: SortOption) => {
    setSelectedOption(option);
    setIsOpen(false);

    dispatchCustomEvent(eventName, { key: option.key });
  }, [dispatchCustomEvent, eventName]);

  const toggleDropdown = useCallback(() => {
    setIsOpen((previousState) => {
      const newState = !previousState;
      if (newState) dispatchCustomEvent('mfe-sort-by-open');
      return newState;
    });
  }, [dispatchCustomEvent]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerReference.current && !containerReference.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="sort-by__control" ref={containerReference}>
      <div className={`sort-by__wrapper${isOpen ? ' sort-by__wrapper--open' : ''}`}>
        <button
          type="button"
          className="sort-by__trigger-button"
          onClick={toggleDropdown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={t('sortBy.label')}
        >

        <div className="sort-by__selected-pill">
          <div className="sort-by__pill-main">
            <BdsIcon name={selectedOption.icon} theme="outline" class="sort-by__pill-icon" />
            <span className="sort-by__pill-text">{t(selectedOption.labelKey)}</span>
          </div>
        </div>
        
        <BdsIcon 
          name="arrow-down" 
          theme="outline" 
          class="sort-by__arrow" 
        />
        </button>
      </div>

      {isOpen && (
        <ul className="sort-by__dropdown" aria-label={t('sortBy.label')}>
          {SORT_OPTIONS.map((option) => (
            <li key={option.key}>
              <button
                className={`sort-by__option${selectedOption?.key === option.key ? ' sort-by__option--active' : ''}`}
                type="button"
                onClick={() => handleOptionSelect(option)}
              >
                <BdsIcon name={option.icon} theme="outline" class="sort-by__option-icon" />
                <span className="sort-by__option-text">{t(option.labelKey)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};